import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { accountSettingsKeys } from '@/hooks/useAccountSettings'

// EP-99 — Operator win-pickup settings hook.
// Dual-gate: Unleash flag `win_pickup` AND settings.win_pickup_enabled.

export const winPickupKeys = {
  all: ['winPickup'] as const,
  credentials: () => [...winPickupKeys.all, 'credentials'] as const,
  bindCode: () => [...winPickupKeys.all, 'bindCode'] as const,
}

// ---- Types ----------------------------------------------------------------

// EP-156 T156-001 — WIN Pickup now has its own LINE keys
// (settings.win_pickup_line_channel_secret / _access_token), separate from
// the tenant OA. Both are write-only; the controller redacts to *_present.
export interface WinPickupCredentials {
  line_channel_secret_present: boolean
  line_channel_access_token_present: boolean
}

export interface WinPickupBindCodeData {
  bind_code: string
  status: 'pending_bind' | 'active' | 'inactive'
  bound_at: string | null
}

// ---- Query hooks -----------------------------------------------------------

export function useWinPickupCredentials() {
  return useQuery({
    queryKey: winPickupKeys.credentials(),
    queryFn: async () => {
      const { data } = await api.get<{ data: WinPickupCredentials }>(
        '/api/win_pickup_settings/line_credentials',
      )
      return data.data
    },
  })
}

export function useWinPickupBindCode() {
  return useQuery({
    queryKey: winPickupKeys.bindCode(),
    queryFn: async () => {
      const { data } = await api.get<{ data: WinPickupBindCodeData }>(
        '/api/win_pickup_settings/bind_code',
      )
      return data.data
    },
  })
}

// ---- Mutation hooks -------------------------------------------------------

export function useUpdateWinPickupCredentials() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: {
      line_channel_secret?: string
      line_channel_access_token?: string
    }) => {
      const { data } = await api.put<{ data: WinPickupCredentials }>(
        '/api/win_pickup_settings/line_credentials',
        params,
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: winPickupKeys.credentials() })
      // Invalidate parent account-settings so any derived gating re-reads.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}

export function useRotateBindCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: WinPickupBindCodeData }>(
        '/api/win_pickup_settings/rotate_bind_code',
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: winPickupKeys.bindCode() })
    },
  })
}

export function useUpdateWinPickupFareBoard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (key: string) => {
      const { data } = await api.put<{ data: { fare_board_url: string; key: string } }>(
        '/api/win_pickup_settings/fare_board',
        { key },
      )
      return data.data
    },
    onSuccess: () => {
      // Invalidate account settings so any fare_board_url references re-fetch.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}
